import { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  setCredentials,
  clearCredentials,
} from "../features/counter/Authslice.js";
import configuration from "../config";
import axios from "axios";

const ProtectedRoute = ({ allowedRoles = [] }) => {
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  const dispatch = useDispatch();
  const { API_BASE_URL } = configuration;

  const { isAuthenticated, user } = useSelector((state) => state.auth);

  useEffect(() => {
    const verifyAuth = async () => {
      try {
        if (isAuthenticated && user) {
          setIsLoading(false);
          return;
        }

        const storedToken = sessionStorage.getItem("accessToken");

        if (storedToken) {
          axios.defaults.headers.common[
            "Authorization"
          ] = `Bearer ${storedToken}`;
        }

        const refreshResponse = await axios.get(
          `${API_BASE_URL}/api/v1/refresh`,
          {
            headers: {
              "Content-Type": "application/json",
            },
            withCredentials: true,
          }
        );

        if (refreshResponse.data.success && refreshResponse.data.token) {
          const newToken = refreshResponse.data.token;

          // Store token in sessionStorage
          sessionStorage.setItem("accessToken", newToken);
          axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;

          dispatch(
            setCredentials({
              token: newToken,
              user: refreshResponse.data.user,
              isAuthenticated: true,
            })
          );
        } else {
          throw new Error("Failed to refresh token");
        }
      } catch (error) {
        console.error("Authentication failed:", error);

        // Clear all auth data
        sessionStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        delete axios.defaults.headers.common["Authorization"];
        dispatch(clearCredentials());
      } finally {
        setIsLoading(false);
      }
    };

    verifyAuth();
  }, [dispatch, API_BASE_URL, isAuthenticated, user]);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            const refreshResponse = await axios.get(
              `${API_BASE_URL}/api/v1/refresh`,
              {
                withCredentials: true,
              }
            );

            if (refreshResponse.data.success && refreshResponse.data.token) {
              const newToken = refreshResponse.data.token;

              sessionStorage.setItem("accessToken", newToken);
              axios.defaults.headers.common[
                "Authorization"
              ] = `Bearer ${newToken}`;

              dispatch(
                setCredentials({
                  token: newToken,
                  user: refreshResponse.data.user,
                  isAuthenticated: true,
                })
              );

              originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
              return axios(originalRequest);
            }
          } catch (refreshError) {
            console.error("Token refresh failed:", refreshError);

            sessionStorage.removeItem("accessToken");
            localStorage.removeItem("refreshToken");
            delete axios.defaults.headers.common["Authorization"];
            dispatch(clearCredentials());

            window.location.href = "/login";
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [dispatch, API_BASE_URL]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(user?.role)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <Outlet />;
};

export default ProtectedRoute;
